// Trip pages (/trips/<project_id>, /trip/<trip_id>, /t/<slug>) move to the
// Astro app project by project. During the canary, Amplify sends only the
// rolled-out projects' /trips/<id> pages (and /trips/rollout.json) to Astro;
// once every project is rolled out it sends every trip URL, and Astro returns
// this app's index.html for anything it doesn't render. GET
// /trips/rollout.json (same origin) lists them: {"all": bool, "projects": [ids]},
// and with "all" the projects kept here ("except": [ids]).
// Date pages (/trip/<trip_id>) are handed off only in that last stage.
//
// This app hands a trip URL to Astro (full page load) only on an in-app
// navigation. The location this document was loaded on always renders <Trip />:
// Astro already chose this app for that URL, so loading it again would loop.
//
// Callers that navigate to /trip/<trip_id> can pass the trip's project as
// router state `{ tripProjectId }`; without it the project is unknown and the
// trip page stays in this app unless every project is rolled out.
//
// Cards and links that know their project hand off at click time
// (handOffTripLink): a native navigation, so the Astro page is a normal
// history entry with this page as its referrer, this page can come back from
// the back-forward cache, and no <LoadingView/> shows in between. TripRoute's
// replace stays as the fallback for router navigations not converted yet.
// Club events are never handed off: Astro answers them with this app's
// index.html, so a hand-off would only load the same page twice.
import { isEmbeddedTrips } from "./embeddedTrips";
import { isClubEvent } from "./clubEvent";
import { saveListState } from "./listState";

const ROLLOUT_URL = "/trips/rollout.json";
const ROLLOUT_TIMEOUT_MS = 3000;

// React Router keys the first location with history.state.key, or "default"
// when there is none (a fresh load). A reload keeps the entry's old key.
const BOOT_HISTORY_KEY = (() => {
  try {
    return window.history.state?.key ?? null;
  } catch {
    return null;
  }
})();

let rollout = { all: false, projects: new Set(), except: new Set() };
let rolloutRequest = null;

// "180", 180 and "180-central-mongolia" are project 180.
const normalizeProjectId = (value) => {
  const match = /^\d+/.exec(String(value ?? "").trim());
  return match ? String(Number(match[0])) : null;
};

// Called once at boot. Never blocks rendering; on an error, a non-JSON answer
// or no answer within 3 s, nothing counts as rolled out.
export const loadAstroTripsRollout = () => {
  if (rolloutRequest) return rolloutRequest;
  if (typeof fetch !== "function") {
    rolloutRequest = Promise.resolve();
    return rolloutRequest;
  }

  let timedOut = false;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = setTimeout(() => {
    timedOut = true;
    controller?.abort();
  }, ROLLOUT_TIMEOUT_MS);

  rolloutRequest = fetch(ROLLOUT_URL, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    signal: controller?.signal,
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      clearTimeout(timer);
      if (timedOut || !data || typeof data !== "object") return;
      const projects = Array.isArray(data.projects) ? data.projects : [];
      const except = Array.isArray(data.except) ? data.except : [];
      rollout = {
        all: data.all === true,
        projects: new Set(projects.map(normalizeProjectId).filter(Boolean)),
        except: new Set(except.map(normalizeProjectId).filter(Boolean)),
      };
    })
    .catch(() => clearTimeout(timer));

  return rolloutRequest;
};

// Whether Astro renders this project's trip pages. An unknown project counts
// only when every project is rolled out (Astro hands an excepted one back).
export const isAstroTripProject = (projectId) => {
  const id = normalizeProjectId(projectId);
  if (rollout.all) return id === null || !rollout.except.has(id);
  return id !== null && rollout.projects.has(id);
};

// Whether Astro answers this trip URL (see the header: date pages only once
// every project is rolled out).
const isAstroTripPath = (pathname, projectId) =>
  /^\/trip\//.test(pathname) ? rollout.all && isAstroTripProject(projectId) : isAstroTripProject(projectId);

// Router state or a card row that is a club event (cards pass broad_category;
// "club" covers every club event, as on the trip page) or a club page link.
const isClubEventRow = (row) =>
  Boolean(row && (row.clubEvent === true || row.broad_category === "club" || isClubEvent(row)));

// Session hint for the Astro trip page's back circle (sotravel_landing
// TripTopBar.tsx): the list URL this tab hands off from, and the trip path.
const HANDOFF_FROM_KEY = "trip-handoff-from";
const HANDOFF_TO_KEY = "trip-handoff-to";
// The last URL of this app that is not a trip page (see TripListTracker in App.js).
let lastListUrl = null;
export const rememberListLocation = (location) => {
  if (!/^\/(trips|trip|t)\//.test(location.pathname)) lastListUrl = location.pathname + location.search;
};
const rememberHandoff = (to, from) => {
  try {
    window.sessionStorage.setItem(HANDOFF_FROM_KEY, from);
    window.sessionStorage.setItem(HANDOFF_TO_KEY, to);
  } catch {
    // Private mode / storage full: the Astro page falls back to the referrer.
  }
};

// Route shim for /trips/:project_id, /trip/:trip_id and /t/:project_slug: the
// URL to load so Astro answers, or null to render <Trip /> here.
export const astroTripHandoffUrl = (location, projectId) => {
  if (location.key === "default" || location.key === BOOT_HISTORY_KEY) return null;
  if (isEmbeddedTrips) return null;
  if (isClubEventRow(location.state)) return null;
  if (!isAstroTripPath(location.pathname, projectId)) return null;

  // "‹ More dates" opens the project page with the dates drawer open. The
  // router already shows this URL, so a #dates-only change would not load the
  // page: Astro reads ?dates=1 as well. Other hashes are dropped for the same
  // reason, and other router state (playVideoWithSound, card preview data) has
  // no URL form.
  const params = new URLSearchParams(location.search);
  if (location.hash === "#dates" || location.state?.openMobileTripDates) params.set("dates", "1");
  const search = params.toString();
  return `${location.pathname}${search ? `?${search}` : ""}`;
};

// TripRoute, just before it loads `url` in place of the router entry: the
// hint for the Astro back circle (the referrer is this same URL) and the list
// state for a cold back.
export const prepareTripRouteHandoff = (url) => {
  rememberHandoff(url, lastListUrl || "/all-trips");
  if (lastListUrl) saveListState(lastListUrl);
};

// Click-time hand-off for a card or link that knows its project: true when it
// navigated (a full load of the Astro page), false to keep the in-app
// navigation. `row` is the card's trip/project row or router state (club
// events stay here). Without `projectId`, a /trips/<id> path names it.
export const handOffTripLink = (path, projectId, row = null) => {
  if (isEmbeddedTrips) return false;
  if (isClubEventRow(row)) return false;
  const pathname = String(path).split(/[?#]/)[0];
  const project = projectId ?? /^\/trips\/([^/]+)/.exec(pathname)?.[1];
  if (!isAstroTripPath(pathname, project)) return false;
  rememberHandoff(path, window.location.pathname + window.location.search);
  saveListState();
  window.location.assign(path);
  return true;
};

// onClick for a router <Link> to a trip page: a plain click on a project the
// Astro app renders loads it natively instead (modifier clicks stay links).
export const tripLinkClick = (path, projectId, row = null) => (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (handOffTripLink(path, projectId, row)) event.preventDefault();
};

// Trip page navigation for code that knows the project (checkout back
// buttons): a full load when Astro renders it, an in-app navigation otherwise.
export const navigateToTripPage = (navigate, path, projectId) => {
  if (isAstroTripPath(path, projectId)) {
    window.location.assign(path);
    return;
  }
  navigate(path);
};

// This document was opened from one of our trip pages (a full load, e.g. the
// Astro page's reserve button).
export const openedFromTripPage = () => {
  try {
    const referrer = new URL(document.referrer);
    return (
      referrer.origin === window.location.origin && /^\/(trips|trip|t)\//.test(referrer.pathname)
    );
  } catch {
    return false;
  }
};
